import React, { useCallback, useMemo } from "react";

import { Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { eduInstitutionColumns } from "./tableParams/eduInstitutionColumns";
import { DELETE_EDU, GET_EDUS } from "../../actions/ActionTypes";
import { delApi, postApi } from "../../actions/SendActions";
import ItemStatus from "../../utils/ItemStatus";
import CappedPage from "../common/CappedPage";
import CreateButton from "../common/CreateButton";
import DataTable from "../dataTable/DataTable";
import DataTableEmptyState from "../dataTable/DataTableEmptyState";
import { eduEditPath, PROJECTS_ROUTE } from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";

import type { JSX } from "react";

import type { EduInstitution, EduInstitutionRow } from "./edu.types";
import type { AppState } from "../../reducers";

function toRow(institution: EduInstitution): EduInstitutionRow {
  return {
    uuid: institution.uuid,
    edu_status: ItemStatus.getEduStatus(institution),
    label: institution.edu_name ?? "",
    user_edu: institution.user_edu.type,
    max_connexion: institution.user_edu.max_session,
    students: institution.user_edu.nbr_users,
    updated_at: institution.updated_at,
  };
}

function EDUAdminInstitutions(): JSX.Element {
  const { t } = useTranslation("editor-edu");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const edus = useSelector((state: AppState) => state.fetchReducer.edus);
  const projectUuid = useSelector(
    (state: AppState) => state.architectureReducer.project_uuid,
  );
  const token = useSelector((state: AppState) => state.loginReducer.token);

  const institutionRowList = useMemo((): EduInstitutionRow[] => {
    const indexList =
      edus.indexes?.storefront_project_uuid?.[projectUuid] ?? [];
    return indexList.reduce<EduInstitutionRow[]>((rowList, index) => {
      const institution = edus.data?.[index];
      if (institution) rowList.push(toRow(institution));
      return rowList;
    }, []);
  }, [edus, projectUuid]);

  const handleDelete = useCallback(
    (institution: EduInstitutionRow): void => {
      dispatch(
        delApi({
          endpoint: `/api/user/v3/admin/users/${institution.uuid}/delete`,
          type: DELETE_EDU,
          item: "edus",
          token,
          message: t("deleteEduComplete"),
          headers: [],
        }),
      );
    },
    [dispatch, token, t],
  );

  const handleRetry = (): void => {
    dispatch(
      postApi({
        endpoint: "/api/user/v3/admin/users/list_users",
        type: GET_EDUS,
        item: "edus",
        token,
        headers: [{ key: "xx-ok-project", value: projectSlug }],
        data: { role: "edu" },
      }),
    );
  };

  const getRowHref = useCallback(
    (institution: EduInstitutionRow): string =>
      eduEditPath(projectSlug, institution.uuid),
    [projectSlug],
  );

  const columnList = useMemo(
    () =>
      eduInstitutionColumns({
        t,
        projectSlug,
        onEdit: (institution) => history.push(getRowHref(institution)),
        onDelete: handleDelete,
      }),
    [t, projectSlug, history, getRowHref, handleDelete],
  );

  const isFirstLoad = Boolean(edus.isFetching && !edus.isFetched);

  const breadcrumbList = [{ name: t("eduTitle") }];

  const createButton = (
    <CreateButton
      href={`${PROJECTS_ROUTE}/${projectSlug}/institutions/new`}
      label={t("createEDU")}
    />
  );

  const emptyState = (
    <DataTableEmptyState
      action={createButton}
      icon={Building2}
      message={t("createEDUText")}
      title={t("eduTitle")}
    />
  );

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Breadcrumbs items={breadcrumbList} />
      <DataTable
        columns={columnList}
        data={institutionRowList}
        defaultSorting={{ id: "updated_at", desc: true }}
        emptyState={emptyState}
        getRowHref={getRowHref}
        headerActions={createButton}
        isError={edus.failed}
        isLoading={isFirstLoad}
        onRetry={handleRetry}
        title={t("eduTitle")}
      />
    </CappedPage>
  );
}

export default EDUAdminInstitutions;
